"use client";

import type {
  LoginAuthenticationResponse,
  LoginProfileResponse,
  LoginScopeResponse,
  MedplumClient,
} from "@medplum/core";
import type { ProjectMembership } from "@medplum/fhirtypes";
import * as Sentry from "@sentry/nextjs";
import { useCallback, useState } from "react";
import { resolveLoginChallenge } from "@/lib/auth/login-flow";
import { isLoginProfileResponse, isLoginScopeResponse } from "@/lib/auth/login-scope";
import { isNonEmptyString } from "@/lib/auth/string-guards";
import { createLogger } from "@/lib/logger";

const logger = createLogger("use-login-challenge-flow");

type LoginStep = "form" | "profile" | "scope";

interface LoginChallengeFlowMessages {
  readonly invalidCredentials: string;
  readonly mfaRequired: string;
  readonly missingSession: string;
  readonly invalidProfileSelection: string;
  readonly genericLoginFailure: string;
}

interface UseLoginChallengeFlowOptions {
  readonly medplum: MedplumClient;
  readonly onCompleteLogin: ({ code }: { code: string }) => Promise<void>;
  readonly onMissingSession?: () => void;
  readonly messages?: Partial<LoginChallengeFlowMessages>;
}

interface UseLoginChallengeFlowResult {
  readonly errorMessage: string;
  readonly loginStep: LoginStep;
  readonly memberships: ProjectMembership[];
  readonly pendingScope?: string;
  readonly isSelectingProfile: boolean;
  readonly isSelectingScope: boolean;
  readonly setErrorMessage: (message: string) => void;
  readonly handleLoginResponse: ({
    response,
  }: {
    response: LoginAuthenticationResponse;
  }) => Promise<void>;
  readonly handleProfileSelection: ({
    membership,
  }: {
    membership: ProjectMembership;
  }) => Promise<void>;
  readonly handleScopeSelection: ({ scope }: { scope: string }) => Promise<void>;
  readonly handleBackToLogin: () => void;
  readonly handleBackToProfile: () => void;
  readonly resetFlow: () => void;
}

const DEFAULT_MESSAGES: LoginChallengeFlowMessages = {
  invalidCredentials: "Login failed. Please check your credentials.",
  mfaRequired: "Multi-factor authentication is required. Please contact support.",
  missingSession: "Missing login session. Please sign in again.",
  invalidProfileSelection: "Invalid profile selection. Please sign in again.",
  genericLoginFailure: "Login failed. Please try again.",
};

const LOGIN_MEMBERSHIP_FILTER_ROUTE = "/api/auth/login-memberships";

interface FilterLoginMembershipsRequestBody {
  loginId: string;
  membershipFilterToken: string;
  membershipIds: string[];
}

interface FilterLoginMembershipsResponseBody {
  activeMembershipIds: string[];
}

async function filterSelectableMemberships({
  loginId,
  membershipFilterToken,
  memberships,
}: {
  loginId: string;
  membershipFilterToken?: string;
  memberships: ProjectMembership[];
}): Promise<ProjectMembership[]> {
  const membershipsWithId = memberships.filter(
    (membership): membership is ProjectMembership & { id: string } =>
      isNonEmptyString(membership.id),
  );
  if (!isNonEmptyString(loginId) || !isNonEmptyString(membershipFilterToken)) {
    return membershipsWithId;
  }

  const membershipIds = membershipsWithId
    .map((membership) => membership.id)
    .filter((membershipId): membershipId is string => isNonEmptyString(membershipId));
  if (membershipIds.length === 0) {
    return membershipsWithId;
  }

  try {
    const requestBody: FilterLoginMembershipsRequestBody = {
      loginId,
      membershipFilterToken,
      membershipIds,
    };
    const response = await fetch(LOGIN_MEMBERSHIP_FILTER_ROUTE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      // Fail closed whenever membership freshness cannot be verified server-side.
      return [];
    }

    const payload = (await response.json()) as Partial<FilterLoginMembershipsResponseBody>;
    const activeMembershipIds = new Set(
      (payload.activeMembershipIds ?? []).filter((membershipId) => isNonEmptyString(membershipId)),
    );
    return membershipsWithId.filter((membership) =>
      membership.id ? activeMembershipIds.has(membership.id) : false,
    );
  } catch {
    // Network/transient errors should not bypass freshness filtering.
    return [];
  }
}

/**
 * Shares profile/scope challenge handling across web login flows.
 */
export function useLoginChallengeFlow({
  medplum,
  onCompleteLogin,
  onMissingSession,
  messages,
}: UseLoginChallengeFlowOptions): UseLoginChallengeFlowResult {
  //------------------------------------------------------------------------------------------------
  //                                          STATE
  //------------------------------------------------------------------------------------------------
  const [errorMessage, setErrorMessageState] = useState("");
  const [loginStep, setLoginStep] = useState<LoginStep>("form");
  const [profileLoginId, setProfileLoginId] = useState<string | undefined>();
  const [scopeLoginId, setScopeLoginId] = useState<string | undefined>();
  const [memberships, setMemberships] = useState<ProjectMembership[]>([]);
  const [pendingScope, setPendingScope] = useState<string | undefined>();
  const [isSelectingProfile, setIsSelectingProfile] = useState(false);
  const [isSelectingScope, setIsSelectingScope] = useState(false);

  //------------------------------------------------------------------------------------------------
  //                                        FUNCTIONS
  //------------------------------------------------------------------------------------------------
  const invalidCredentialsMessage =
    messages?.invalidCredentials ?? DEFAULT_MESSAGES.invalidCredentials;
  const mfaRequiredMessage = messages?.mfaRequired ?? DEFAULT_MESSAGES.mfaRequired;
  const missingSessionMessage = messages?.missingSession ?? DEFAULT_MESSAGES.missingSession;
  const invalidProfileSelectionMessage =
    messages?.invalidProfileSelection ?? DEFAULT_MESSAGES.invalidProfileSelection;
  const genericLoginFailureMessage =
    messages?.genericLoginFailure ?? DEFAULT_MESSAGES.genericLoginFailure;

  /**
   * Returns a user-friendly message for unknown thrown values.
   */
  const toErrorMessage = useCallback(
    ({ error }: { error: unknown }): string => {
      return error instanceof Error ? error.message : genericLoginFailureMessage;
    },
    [genericLoginFailureMessage],
  );

  /**
   * Resets all challenge-selection state to the form step.
   */
  const resetChallengeState = useCallback(() => {
    setLoginStep("form");
    setProfileLoginId(undefined);
    setScopeLoginId(undefined);
    setMemberships([]);
    setPendingScope(undefined);
    setIsSelectingProfile(false);
    setIsSelectingScope(false);
  }, []);

  /**
   * Resets challenge state and clears current errors.
   */
  const resetFlow = useCallback(() => {
    resetChallengeState();
    setErrorMessageState("");
  }, [resetChallengeState]);

  /**
   * Handles recoverable invalid-session states.
   */
  const handleMissingSession = useCallback(
    ({ message }: { message: string }) => {
      setErrorMessageState(message);
      resetChallengeState();
      onMissingSession?.();
    },
    [onMissingSession, resetChallengeState],
  );

  /**
   * Handles the response from startLogin/startGoogleLogin.
   */
  const handleLoginResponse = useCallback(
    async ({ response }: { response: LoginAuthenticationResponse }) => {
      setErrorMessageState("");

      const challenge = resolveLoginChallenge({ response });
      if (challenge.status === "requires-mfa") {
        setErrorMessageState(mfaRequiredMessage);
        return;
      }

      if (challenge.status === "complete") {
        try {
          await onCompleteLogin({ code: challenge.code });
        } catch (error) {
          logger.error("Error completing login challenge", { error });
          resetChallengeState();
          setErrorMessageState(toErrorMessage({ error }));
          // Let callers run flow-specific fallback behavior (e.g. redirect/cleanup).
          throw error;
        }
        return;
      }

      if (challenge.status === "select-profile") {
        const selectableMemberships = await filterSelectableMemberships({
          loginId: challenge.loginId,
          membershipFilterToken: challenge.membershipFilterToken,
          memberships: challenge.memberships,
        });

        // No selectable memberships after freshness filtering means ProjectMembership.active===false
        // for all returned memberships — the user lands on an empty profile picker with no error
        // message. Log here so we can diagnose in production.
        if (selectableMemberships.length === 0 && challenge.memberships.length > 0) {
          const membershipIds = challenge.memberships.map((m) => m.id).filter(isNonEmptyString);
          const profileRefs = challenge.memberships
            .map((m) => m.profile?.reference)
            .filter(isNonEmptyString);

          logger.warn(
            "Login blocked: all ProjectMemberships filtered out — likely ProjectMembership.active===false",
            {
              loginId: challenge.loginId,
              membershipIds,
              profileRefs,
              candidateMembershipCount: challenge.memberships.length,
            },
          );

          Sentry.withScope((scope) => {
            scope.setTag("feature", "login-flow");
            scope.setTag("vibrant.loginId", challenge.loginId);
            if (membershipIds[0]) scope.setTag("vibrant.membershipId", membershipIds[0]);
            scope.setLevel("warning");
            scope.setContext("login_membership_filter", {
              loginId: challenge.loginId,
              membershipIds,
              profileRefs,
              candidateMembershipCount: challenge.memberships.length,
            });
            Sentry.captureMessage(
              "Login blocked: all ProjectMemberships filtered out — likely ProjectMembership.active===false",
              "warning",
            );
          });
        }

        setProfileLoginId(challenge.loginId);
        setScopeLoginId(undefined);
        setMemberships(selectableMemberships);
        setPendingScope(undefined);

        const singleMembership = selectableMemberships[0];
        if (selectableMemberships.length === 1 && singleMembership?.id) {
          const selectedMembershipId = singleMembership.id;
          setIsSelectingProfile(true);
          try {
            const autoSelectionResponse = (await medplum.post("auth/profile", {
              login: challenge.loginId,
              profile: selectedMembershipId,
            })) as LoginProfileResponse | LoginScopeResponse;

            if (isLoginScopeResponse(autoSelectionResponse)) {
              await onCompleteLogin({ code: autoSelectionResponse.code });
              return;
            }

            if (isLoginProfileResponse(autoSelectionResponse)) {
              setScopeLoginId(autoSelectionResponse.login);
              setPendingScope(autoSelectionResponse.scope);
              setLoginStep("scope");
              return;
            }

            throw new Error(genericLoginFailureMessage);
          } catch (error) {
            setErrorMessageState(toErrorMessage({ error }));
          } finally {
            setIsSelectingProfile(false);
          }
        }

        setLoginStep("profile");
        return;
      }

      setErrorMessageState(invalidCredentialsMessage);
    },
    [
      invalidCredentialsMessage,
      genericLoginFailureMessage,
      mfaRequiredMessage,
      medplum,
      onCompleteLogin,
      resetChallengeState,
      toErrorMessage,
    ],
  );

  /**
   * Handles profile selection for multi-profile logins.
   */
  const handleProfileSelection = useCallback(
    async ({ membership }: { membership: ProjectMembership }) => {
      if (!profileLoginId) {
        handleMissingSession({ message: missingSessionMessage });
        return;
      }

      if (!membership.id) {
        handleMissingSession({ message: invalidProfileSelectionMessage });
        return;
      }

      setIsSelectingProfile(true);
      setErrorMessageState("");

      try {
        const response = (await medplum.post("auth/profile", {
          login: profileLoginId,
          profile: membership.id,
        })) as LoginProfileResponse | LoginScopeResponse;

        if (isLoginScopeResponse(response)) {
          await onCompleteLogin({ code: response.code });
          return;
        }

        if (isLoginProfileResponse(response)) {
          setScopeLoginId(response.login);
          setPendingScope(response.scope);
          setLoginStep("scope");
          return;
        }

        throw new Error(genericLoginFailureMessage);
      } catch (error) {
        logger.error("Error selecting profile during login challenge", { error });
        setErrorMessageState(toErrorMessage({ error }));
      } finally {
        setIsSelectingProfile(false);
      }
    },
    [
      genericLoginFailureMessage,
      handleMissingSession,
      invalidProfileSelectionMessage,
      medplum,
      missingSessionMessage,
      onCompleteLogin,
      profileLoginId,
      toErrorMessage,
    ],
  );

  /**
   * Handles scope selection after profile selection.
   */
  const handleScopeSelection = useCallback(
    async ({ scope }: { scope: string }) => {
      const activeScopeLoginId = scopeLoginId ?? profileLoginId;
      if (!activeScopeLoginId) {
        handleMissingSession({ message: missingSessionMessage });
        return;
      }

      setIsSelectingScope(true);
      setErrorMessageState("");

      try {
        const response = (await medplum.post("auth/scope", {
          login: activeScopeLoginId,
          scope,
        })) as LoginScopeResponse;

        if (!isNonEmptyString(response.code)) {
          throw new Error(genericLoginFailureMessage);
        }

        await onCompleteLogin({ code: response.code });
      } catch (error) {
        logger.error("Error selecting scope during login challenge", { error });
        setErrorMessageState(toErrorMessage({ error }));
      } finally {
        setIsSelectingScope(false);
      }
    },
    [
      genericLoginFailureMessage,
      handleMissingSession,
      medplum,
      missingSessionMessage,
      onCompleteLogin,
      profileLoginId,
      scopeLoginId,
      toErrorMessage,
    ],
  );

  /**
   * Returns from profile selection to sign-in form.
   */
  const handleBackToLogin = useCallback(() => {
    resetFlow();
    onMissingSession?.();
  }, [onMissingSession, resetFlow]);

  /**
   * Returns from scope selection back to profile selection.
   */
  const handleBackToProfile = useCallback(() => {
    setLoginStep("profile");
    setScopeLoginId(undefined);
    setPendingScope(undefined);
    setErrorMessageState("");
  }, []);

  return {
    errorMessage,
    loginStep,
    memberships,
    pendingScope,
    isSelectingProfile,
    isSelectingScope,
    setErrorMessage: setErrorMessageState,
    handleLoginResponse,
    handleProfileSelection,
    handleScopeSelection,
    handleBackToLogin,
    handleBackToProfile,
    resetFlow,
  };
}
